/**
 * User + auth response types.
 *
 * Ported from legacy `frontend/src/types.ts` (User interface) plus the shape
 * returned by `POST /api/auth/google`, `POST /api/auth/refresh`, `GET /api/auth/me`
 * as observed in `backend/src/routes/auth.ts`.
 */

import { z } from 'zod';

export const userSchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string().nullable().optional(),
  householdId: z.string(),
  householdName: z.string().optional(),
  inviteCode: z.string().optional(),
  profilePhotoUrl: z.string().nullable().optional(),
  // T28 — platform-admin flag derived from the signed JWT email claim. Optional
  // so older servers / cached payloads validate; the consumer ☰ shows a plain
  // /admin link only when this is true. The server `requireAdmin` gate is the
  // real boundary, not this flag.
  isAdmin: z.boolean().optional(),
  // T48 — whether the account's email is confirmed. Drives the in-app
  // confirm-email nudge (shown only when this is false). Optional so older
  // servers / cached payloads validate; absent ⇒ treated as unverified.
  emailVerified: z.boolean().optional(),
});
export type User = z.infer<typeof userSchema>;

/**
 * Response shape from `POST /api/auth/google`.
 * `sessionToken` is the short-lived JWT access token; the refresh token is
 * delivered via an HttpOnly cookie set by the backend.
 */
export const googleAuthResponseSchema = z.object({
  success: z.boolean(),
  inviteError: z.string().optional(),
  user: userSchema,
  sessionToken: z.string(),
  // T27.2.2.2 — native-only. When the login request signals native (`native:
  // true`), the backend returns the refresh token in the body so the native
  // adapter can store it in the Android Keystore (the WebView cookie drops
  // cross-site). Omitted on the web path, where the httpOnly cookie carries it.
  refreshToken: z.string().optional(),
});
export type GoogleAuthResponse = z.infer<typeof googleAuthResponseSchema>;

/**
 * T49.2 — the `/google` "consent required" response. A NEW Google account
 * requires explicit Terms + Privacy consent; when the client signs in a new user
 * WITHOUT the `termsAccepted` flag, the backend creates nothing and returns
 * `200 { consentRequired: true }` (a distinct, detectable shape — not the session
 * body, not an error status). The client surfaces the "Almost there" consent step
 * and re-calls `/google` with `termsAccepted: true`. A returning user never sees
 * this. Distinct from the success schema so a caller can `safeParse` and branch.
 */
export const googleConsentRequiredSchema = z.object({
  consentRequired: z.literal(true),
});
export type GoogleConsentRequired = z.infer<typeof googleConsentRequiredSchema>;

/**
 * The full `/google` outcome: either a session (success) OR the consent-required
 * signal. `AuthApi.loginWithGoogle` returns this union so the caller branches on
 * `'consentRequired' in result`.
 */
export const googleAuthOutcomeSchema = z.union([
  googleConsentRequiredSchema,
  googleAuthResponseSchema,
]);
export type GoogleAuthOutcome = z.infer<typeof googleAuthOutcomeSchema>;

/**
 * Response shape from `POST /api/auth/refresh`.
 * The new refresh token is set via HttpOnly cookie; the response body carries
 * only the new access token.
 */
export const refreshResponseSchema = z.object({
  sessionToken: z.string(),
  // T28 — admin flag surfaced on refresh too (optional; see userSchema).
  isAdmin: z.boolean().optional(),
  // T27.2.2.2 — native-only rotated refresh token (see googleAuthResponseSchema).
  refreshToken: z.string().optional(),
});
export type RefreshResponse = z.infer<typeof refreshResponseSchema>;

/**
 * Response shape from `POST /api/auth/logout`.
 */
export const logoutResponseSchema = z.object({
  success: z.boolean(),
});
export type LogoutResponse = z.infer<typeof logoutResponseSchema>;

/**
 * Request body for `POST /api/auth/google`.
 */
export const googleAuthRequestSchema = z.object({
  credential: z.string().min(1),
  inviteToken: z.string().optional(),
  // T27.2.2.2 — native signal for initial login. There is no refresh token yet
  // to echo via a header, so the native client sets this flag; the backend then
  // returns the refresh token in the body (Keystore-bound). Web omits it and
  // keeps the httpOnly cookie path.
  native: z.boolean().optional(),
  // T49.2 — explicit Terms + Privacy consent. Sent only when CREATING a new
  // account (the "Almost there" consent step sets it true); a returning user
  // omits it and is never re-asked. Absent for a new account → the backend
  // returns `{ consentRequired: true }`.
  termsAccepted: z.boolean().optional(),
});
export type GoogleAuthRequest = z.infer<typeof googleAuthRequestSchema>;

/**
 * Request body for `POST /api/auth/login` (T27.2.10.1 — email + password).
 * `native` mirrors the Google flow: when set the backend returns the refresh
 * token in the body (Keystore-bound) instead of relying on the cookie.
 */
export const emailLoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  inviteToken: z.string().optional(),
  native: z.boolean().optional(),
});
export type EmailLoginRequest = z.infer<typeof emailLoginRequestSchema>;

/**
 * Response shape from `POST /api/auth/login`. Identical to the Google response
 * (both go through the shared `issueSession` helper), so it reuses the same
 * schema — kept as a named alias so callers read intent.
 */
export const emailLoginResponseSchema = googleAuthResponseSchema;
export type EmailLoginResponse = z.infer<typeof emailLoginResponseSchema>;

/**
 * Response from `GET /api/auth/identities` (T27.2.10.1) — the sign-in methods
 * linked to the authenticated account. Drives the Settings link/unlink UI:
 * "Set password" vs "Change password", "Connect Google", and whether an unlink
 * is allowed (the server also enforces the last-identity guard).
 */
export const authIdentitiesResponseSchema = z.object({
  providers: z.array(z.string()),
  hasPassword: z.boolean(),
  hasGoogle: z.boolean(),
});
export type AuthIdentitiesResponse = z.infer<typeof authIdentitiesResponseSchema>;

/** Request body for `POST /api/auth/set-password`. */
export const setPasswordRequestSchema = z.object({
  password: z.string().min(1),
});
export type SetPasswordRequest = z.infer<typeof setPasswordRequestSchema>;

/** Generic `{ success: true }` ack (set-password / unlink). */
export const authSuccessResponseSchema = z.object({ success: z.boolean() });
export type AuthSuccessResponse = z.infer<typeof authSuccessResponseSchema>;

// ---------------------------------------------------------------------------
// T27.2.10.1 Phase B — public registration + verification + password reset.
// ---------------------------------------------------------------------------

/**
 * Request body for `POST /api/auth/register`. The Turnstile token is verified
 * server-side. T48: register now provisions the account AND issues a session, so
 * `native` matters here too — when set the backend returns the refresh token in
 * the body (Keystore-bound) instead of relying on the cookie.
 */
export const registerRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  turnstileToken: z.string().min(1),
  native: z.boolean().optional(),
  // T49.2 — explicit Terms + Privacy consent. The backend requires this to be
  // `true`; the Create button gates on the ticked checkbox, so a valid submit
  // always carries it. A missing/false value is a clean 400 (no account created).
  termsAccepted: z.boolean(),
});
export type RegisterRequest = z.infer<typeof registerRequestSchema>;

/**
 * Response from `POST /api/auth/register` (T48). Register now provisions + logs
 * in, so it returns the SAME session body as the Google/email login response.
 * An already-registered email returns a 409 (surfaced as an `ApiError`), not
 * this body.
 */
export const registerResponseSchema = googleAuthResponseSchema;
export type RegisterResponse = z.infer<typeof registerResponseSchema>;

/**
 * Response from `POST /api/auth/resend-verification` (T48). A generic ack — the
 * link was (re)sent for the signed-in user's email. Same generic-inbox shape.
 */
export const resendVerificationResponseSchema = z.object({
  ok: z.boolean(),
  message: z.string(),
});
export type ResendVerificationResponse = z.infer<typeof resendVerificationResponseSchema>;

/**
 * Response from `POST /api/auth/register` + `POST /api/auth/request-reset`. The
 * SAME generic shape regardless of whether the email exists (no enumeration) —
 * the client only learns "we accepted the request, check your inbox".
 */
export const genericInboxResponseSchema = z.object({
  ok: z.boolean(),
  message: z.string(),
});
export type GenericInboxResponse = z.infer<typeof genericInboxResponseSchema>;

/** Request body for `POST /api/auth/verify` (consume a verification token). */
export const verifyRequestSchema = z.object({
  token: z.string().min(1),
  native: z.boolean().optional(),
});
export type VerifyRequest = z.infer<typeof verifyRequestSchema>;

/**
 * Response from `POST /api/auth/verify` (T48). An UNAUTHENTICATED verify (the
 * link clicked in a fresh browser) issues a session — the full login shape. An
 * already-AUTHENTICATED verify (the in-app nudge link) returns only an ack
 * (`{ success, emailVerified }`, no session). So `user`/`sessionToken` are
 * optional here; the caller applies the session only when present.
 */
export const verifyResponseSchema = z.object({
  success: z.boolean(),
  emailVerified: z.boolean().optional(),
  inviteError: z.string().optional(),
  user: userSchema.optional(),
  sessionToken: z.string().optional(),
  refreshToken: z.string().optional(),
});
export type VerifyResponse = z.infer<typeof verifyResponseSchema>;

/**
 * Response from `POST /api/auth/reset` — always issues a session, so it reuses
 * the Google/email login response shape (kept distinct from `verify`, which can
 * ack-only for an authenticated caller).
 */
export const resetResponseSchema = googleAuthResponseSchema;
export type ResetResponse = z.infer<typeof resetResponseSchema>;

/** Request body for `POST /api/auth/request-reset` (forgot password). */
export const requestResetRequestSchema = z.object({
  email: z.string().email(),
});
export type RequestResetRequest = z.infer<typeof requestResetRequestSchema>;

/** Request body for `POST /api/auth/reset` (complete a password reset). */
export const resetRequestSchema = z.object({
  token: z.string().min(1),
  password: z.string().min(1),
  native: z.boolean().optional(),
});
export type ResetRequest = z.infer<typeof resetRequestSchema>;
